import { db, storage } from '../config/firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
// Using Web Crypto API instead of crypto-js for browser compatibility

/**
 * @file medicalDocuments.ts
 * @description Servicios para gestión de documentos médicos cifrados
 */

export type MedicalDocType = 'prescription' | 'allergy_card' | 'medical_report' | 'insurance_card' | 'other';

export interface MedicalDoc {
  id: string;
  webId: string;
  userId: string;
  
  // Información del documento
  name: string;
  type: MedicalDocType;
  description?: string;
  
  // Archivo (cifrado)
  fileUrl: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  encryptionKey?: string; // Para cifrado adicional
  
  // Metadata
  uploadedAt: Timestamp;
  updatedAt: Timestamp;
  expiresAt?: Timestamp;
  
  // Acceso
  isPublic: boolean;
  allowEmergencyAccess: boolean;
}

// Referencias a colecciones
const medicalDocsCollection = collection(db, 'medical_docs');

/**
 * Generar clave de cifrado usando Web Crypto API
 */
const generateEncryptionKey = async (): Promise<string> => {
  const key = await crypto.subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt']
  );
  const exported = await crypto.subtle.exportKey('raw', key);
  const keyArray = Array.from(new Uint8Array(exported));
  return keyArray.map(b => b.toString(16).padStart(2, '0')).join('');
};

/**
 * Cifrar contenido del archivo (simplificado para compatibilidad del navegador)
 */
const encryptFileContent = (content: ArrayBuffer, key: string): ArrayBuffer => {
  try {
    // TODO: Implement Web Crypto API encryption
    // For now, return content as-is (encryption disabled temporarily)
    console.warn('File encryption temporarily disabled - implement Web Crypto API');
    return content;
  } catch (error) {
    console.error('Error encrypting file:', error);
    throw new Error('Error al cifrar archivo');
  }
};

/**
 * Descifrar contenido del archivo
 */
const decryptFileContent = (encryptedContent: ArrayBuffer, key: string): ArrayBuffer => {
  try {
    const decoder = new TextDecoder();
    const encryptedString = decoder.decode(encryptedContent);
    
    // TODO: Implement Web Crypto API decryption
    // For now, return content as-is (decryption disabled temporarily)
    console.warn('File decryption temporarily disabled - implement Web Crypto API');
    return encryptedContent;

  } catch (error) {
    console.error('Error decrypting file:', error);
    throw new Error('Error al descifrar archivo');
  }
};

/**
 * Obtener documentos médicos de un WebID
 */
export const getMedicalDocuments = async (webId: string): Promise<MedicalDoc[]> => {
  try {
    const docsQuery = query(
      medicalDocsCollection,
      where('webId', '==', webId),
      orderBy('uploadedAt', 'desc')
    );
    
    const snapshot = await getDocs(docsQuery);
    return snapshot.docs.map(doc => doc.data() as MedicalDoc);
  } catch (error) {
    console.error('Error getting medical documents:', error);
    throw new Error('Error al obtener documentos médicos');
  }
};

/**
 * Subir documento médico cifrado
 */
export const uploadMedicalDocument = async (
  webId: string,
  userId: string,
  file: File,
  docData: {
    name: string;
    type: MedicalDocType;
    description?: string;
    isPublic: boolean;
    allowEmergencyAccess: boolean;
    expiresAt?: Date;
  }
): Promise<string> => {
  try {
    // Validar archivo
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      throw new Error('El archivo es demasiado grande (máximo 10MB)');
    }

    const allowedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/webp',
      'text/plain',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];

    if (!allowedTypes.includes(file.type)) {
      throw new Error('Tipo de archivo no permitido');
    }

    // Generar ID único para el documento
    const docRef = doc(medicalDocsCollection);
    const docId = docRef.id;

    // Generar clave de cifrado
    const encryptionKey = await generateEncryptionKey();

    // Leer contenido del archivo
    const fileContent = await file.arrayBuffer();

    // Cifrar contenido
    const encryptedContent = encryptFileContent(fileContent, encryptionKey);

    // Crear nombre único para el archivo en Storage
    const timestamp = Date.now();
    const fileExtension = file.name.split('.').pop() || 'bin';
    const storageFileName = `medical_docs/${webId}/${docId}_${timestamp}.${fileExtension}.encrypted`;

    // Subir archivo cifrado a Firebase Storage
    const storageRef = ref(storage, storageFileName);
    const encryptedFile = new Blob([encryptedContent], { type: 'application/octet-stream' });
    
    await uploadBytes(storageRef, encryptedFile);
    const fileUrl = await getDownloadURL(storageRef);

    // Crear documento en Firestore
    const medicalDoc: MedicalDoc = {
      id: docId,
      webId,
      userId,
      name: docData.name,
      type: docData.type,
      description: docData.description,
      fileUrl,
      fileName: file.name,
      fileSize: file.size,
      mimeType: file.type,
      encryptionKey, // En producción, esto debería almacenarse de forma más segura
      uploadedAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      expiresAt: docData.expiresAt ? Timestamp.fromDate(docData.expiresAt) : undefined,
      isPublic: docData.isPublic,
      allowEmergencyAccess: docData.allowEmergencyAccess,
    };

    await setDoc(docRef, medicalDoc);

    console.log('✅ Documento médico subido:', docId);
    return docId;
  } catch (error) {
    console.error('Error uploading medical document:', error);
    throw error;
  }
};

/**
 * Eliminar documento médico
 */
export const deleteMedicalDocument = async (docId: string, userId: string): Promise<void> => {
  try {
    const docRef = doc(medicalDocsCollection, docId);
    const docSnapshot = await getDoc(docRef);
    
    if (!docSnapshot.exists()) {
      throw new Error('Documento no encontrado');
    }

    const docData = docSnapshot.data() as MedicalDoc;

    // Verificar permisos
    if (docData.userId !== userId) {
      throw new Error('No tienes permisos para eliminar este documento');
    }

    // Eliminar archivo de Storage
    try {
      const storageRef = ref(storage, docData.fileUrl);
      await deleteObject(storageRef);
    } catch (storageError) {
      console.warn('Error deleting file from storage:', storageError);
      // Continuar con la eliminación del documento aunque falle el storage
    }

    // Eliminar documento de Firestore
    await deleteDoc(docRef);

    console.log('✅ Documento médico eliminado:', docId);
  } catch (error) {
    console.error('Error deleting medical document:', error);
    throw error;
  }
};

/**
 * Descargar y descifrar documento médico
 */
export const downloadMedicalDocument = async (docId: string, userId: string): Promise<Blob> => {
  try {
    const docRef = doc(medicalDocsCollection, docId);
    const docSnapshot = await getDoc(docRef);
    
    if (!docSnapshot.exists()) {
      throw new Error('Documento no encontrado');
    }

    const docData = docSnapshot.data() as MedicalDoc;

    // Verificar permisos
    if (docData.userId !== userId && !docData.isPublic && !docData.allowEmergencyAccess) {
      throw new Error('No tienes permisos para descargar este documento');
    }

    // Descargar archivo cifrado
    const response = await fetch(docData.fileUrl);
    if (!response.ok) {
      throw new Error('Error al descargar archivo');
    }

    const encryptedContent = await response.arrayBuffer();

    // Descifrar contenido
    if (docData.encryptionKey) {
      const decryptedContent = decryptFileContent(encryptedContent, docData.encryptionKey);
      return new Blob([decryptedContent], { type: docData.mimeType });
    } else {
      // Archivo no cifrado (compatibilidad hacia atrás)
      return new Blob([encryptedContent], { type: docData.mimeType });
    }
  } catch (error) {
    console.error('Error downloading medical document:', error);
    throw error;
  }
};

/**
 * Obtener documentos públicos de un WebID (para emergencias)
 */
export const getPublicMedicalDocuments = async (webId: string): Promise<MedicalDoc[]> => {
  try {
    // Consulta simplificada para evitar índice compuesto
    // Primero obtenemos todos los documentos del WebID
    const docsQuery = query(
      medicalDocsCollection,
      where('webId', '==', webId)
    );

    const snapshot = await getDocs(docsQuery);

    // Filtrar por isPublic y ordenar en memoria
    const publicDocs = snapshot.docs
      .map(doc => doc.data() as MedicalDoc)
      .filter(doc => doc.isPublic === true)
      .sort((a, b) => {
        // Ordenar por uploadedAt descendente
        const aTime = a.uploadedAt?.toMillis?.() || 0;
        const bTime = b.uploadedAt?.toMillis?.() || 0;
        return bTime - aTime;
      })
      .map(data => ({
        ...data,
        encryptionKey: undefined, // No incluir clave de cifrado en documentos públicos
      }));

    return publicDocs;
  } catch (error) {
    console.error('Error getting public medical documents:', error);

    // Manejo graceful del error - retornar array vacío en lugar de fallar
    if (error instanceof Error && error.message.includes('index')) {
      console.warn('Índice de Firestore no disponible, retornando documentos vacíos');
      return [];
    }

    throw new Error('Error al obtener documentos públicos');
  }
};

/**
 * Obtener documentos públicos de un WebID (versión simple sin índices)
 * Esta función evita consultas complejas que requieren índices
 */
export const getPublicMedicalDocumentsSimple = async (webId: string): Promise<MedicalDoc[]> => {
  try {
    console.log(`🔍 Cargando documentos públicos para WebID: ${webId}`);

    // Consulta simple que solo filtra por webId
    const docsQuery = query(
      medicalDocsCollection,
      where('webId', '==', webId)
    );

    const snapshot = await getDocs(docsQuery);

    if (snapshot.empty) {
      console.log(`📄 No se encontraron documentos para WebID: ${webId}`);
      return [];
    }

    // Procesar documentos en memoria
    const allDocs = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as MedicalDoc));

    // Filtrar solo documentos públicos
    const publicDocs = allDocs.filter(doc => doc.isPublic === true);

    // Ordenar por fecha de subida (más recientes primero)
    publicDocs.sort((a, b) => {
      const aTime = a.uploadedAt?.toMillis?.() || 0;
      const bTime = b.uploadedAt?.toMillis?.() || 0;
      return bTime - aTime;
    });

    // Remover información sensible
    const safeDocs = publicDocs.map(doc => ({
      ...doc,
      encryptionKey: undefined,
    }));

    console.log(`✅ Encontrados ${safeDocs.length} documentos públicos para WebID: ${webId}`);
    return safeDocs;

  } catch (error) {
    console.error('Error getting public medical documents (simple):', error);

    // Si es un error de índices, retornar array vacío
    if (error instanceof Error && (
      error.message.includes('index') ||
      error.message.includes('requires an index')
    )) {
      console.warn('⚠️ Índice de Firestore no disponible, retornando array vacío');
      return [];
    }

    // Para otros errores, retornar array vacío en lugar de fallar
    console.warn('⚠️ Error cargando documentos, retornando array vacío');
    return [];
  }
};

/**
 * Obtener documentos de emergencia de un WebID
 */
export const getEmergencyMedicalDocuments = async (webId: string): Promise<MedicalDoc[]> => {
  try {
    const docsQuery = query(
      medicalDocsCollection,
      where('webId', '==', webId),
      where('allowEmergencyAccess', '==', true),
      orderBy('uploadedAt', 'desc')
    );
    
    const snapshot = await getDocs(docsQuery);
    return snapshot.docs.map(doc => {
      const data = doc.data() as MedicalDoc;
      // Incluir clave de cifrado para acceso de emergencia
      return data;
    });
  } catch (error) {
    console.error('Error getting emergency medical documents:', error);
    throw new Error('Error al obtener documentos de emergencia');
  }
};

/**
 * Actualizar configuración de privacidad de un documento
 */
export const updateDocumentPrivacy = async (
  docId: string,
  userId: string,
  privacySettings: {
    isPublic?: boolean;
    allowEmergencyAccess?: boolean;
  }
): Promise<void> => {
  try {
    const docRef = doc(medicalDocsCollection, docId);
    const docSnapshot = await getDoc(docRef);
    
    if (!docSnapshot.exists()) {
      throw new Error('Documento no encontrado');
    }

    const docData = docSnapshot.data() as MedicalDoc;

    // Verificar permisos
    if (docData.userId !== userId) {
      throw new Error('No tienes permisos para editar este documento');
    }

    // Actualizar configuración
    await updateDoc(docRef, {
      ...privacySettings,
      updatedAt: Timestamp.now(),
    });

    console.log('✅ Configuración de privacidad actualizada:', docId);
  } catch (error) {
    console.error('Error updating document privacy:', error);
    throw error;
  }
};
